import * as React from "react";

const DANISH_NAME_FIXES: Record<string, string> = {
  "Jorgen Leth": "Jørgen Leth",
  "Soren Kierkegaard": "Søren Kierkegaard",
  "Benny Andersen": "Benny Andersen",
};

function normalizePersonName(name: string): string {
  const cleaned = name
    .replace(/_/g, " ")
    .replace(/\s*\([^)]*\)\s*$/g, "")
    .replace(/\s*,\s*.*$/, "")
    .trim();

  return DANISH_NAME_FIXES[cleaned] ?? cleaned;
}

/**
 * Byg et Google-søgnings-URL for et personnavn.
 */
export function googleSearchUrl(name: string): string {
  const cleaned = normalizePersonName(name);
  return `https://www.google.com/search?q=${encodeURIComponent(cleaned)}`;
}

/**
 * En React-komponent der viser et personnavn som et diskret link til en
 * Google-søgning på navnet.
 */
export function WikiName({
  name,
  className,
}: {
  name: string;
  className?: string;
}) {
  if (!name?.trim()) return null;
  const normalized = normalizePersonName(name);
  return (
    <a
      href={googleSearchUrl(normalized)}
      target="_blank"
      rel="noopener noreferrer"
      className={
        className ??
        "underline decoration-rule underline-offset-2 hover:decoration-bordeaux hover:text-bordeaux transition-colors duration-300"
      }
      title={`Søg efter ${normalized} på Google`}
    >
      {name}
    </a>
  );
}

/**
 * Render en tekst der må indeholde markdown-links af formen [tekst](https://…).
 * Kun links til wikipedia.org accepteres fra kilden (whitelist mod fremmede
 * links fra AI-genereret indhold), men vi rendrer dem som en Google-søgning
 * på navnet i stedet for et Wikipedia-link. Bevar mellemrum og linjeskift.
 */
export function renderWithWikiLinks(text: string): React.ReactNode {
  if (!text) return null;
  const re = /\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)/g;
  const out: React.ReactNode[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  let key = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const [full, label, url] = m;
    const isWiki = /^https?:\/\/[a-z]{2,3}\.wikipedia\.org\/wiki\//i.test(url);
    if (isWiki) {
      const normalizedLabel = normalizePersonName(label);
      out.push(
        <a
          key={`w${key++}`}
          href={googleSearchUrl(normalizedLabel)}
          target="_blank"
          rel="noopener noreferrer"
          className="underline decoration-rule underline-offset-2 hover:decoration-bordeaux hover:text-bordeaux transition-colors duration-300"
          title={`Søg efter ${normalizedLabel} på Google`}
        >
          {normalizedLabel}
        </a>,
      );
    } else {
      out.push(label);
    }
    last = m.index + full.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}
